// PeckerTapLanding.jsx
// Fully mobile-optimized Pecker Tap landing page.
// Self-contained — no external deps beyond React.
// Add to index.html:
//   <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=DM+Sans:wght@300;400;500;600&display=swap" rel="stylesheet">
//   <meta name="viewport" content="width=device-width, initial-scale=1.0">

import React, { useEffect, useRef, useCallback, useState } from "react";
import { FaTwitter, FaMusic, FaChartBar, FaBolt, FaMobileAlt, FaBullseye } from "react-icons/fa";
import PeckerTapGame from "./PeckerTapGame";

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  night:   "#0D0F1A",
  night2:  "#13162A",
  night3:  "#1C2040",
  yellow:  "#FFD93D",
  orange:  "#FF8C00",
  red:     "#FF6B35",
  sky:     "#87CEEB",
  green:   "#5CBB3C",
  greenDk: "#38801E",
  muted:   "rgba(255,255,255,0.5)",
  pixel:   "'Press Start 2P', monospace",
  body:    "'DM Sans', sans-serif",
};

// ─────────────────────────────────────────────────────────────────────────────
// GAME ENGINE
// ─────────────────────────────────────────────────────────────────────────────
const GH = 64, PW = 52, PHC = 16;
const C = {
  sky1:"#87CEEB", sky2:"#B3E5FC",
  cloud:"#FFF", cloudS:"#D8EEF8",
  ground:"#5D8A3C", groundS:"#4A7A2E", dirt:"#8B6355",
  pL:"#70D44A", pM:"#5CBB3C", pD:"#38801E",
  bB2:"#FFA800", bB1:"#FFD93D", bBk:"#FF6B35", bW:"#FF8C00",
  pA:"#FFD93D", pB:"#FF6B35",
};

let _ac = null;
function getAC() {
  if (!_ac) _ac = new (window.AudioContext || window.webkitAudioContext)();
  if (_ac.state === "suspended") _ac.resume();
  return _ac;
}
function osc(type, freq, gain, dur, freqEnd, t0) {
  const ac = getAC(), t = t0 ?? ac.currentTime;
  const o = ac.createOscillator(), g = ac.createGain();
  o.connect(g); g.connect(ac.destination);
  o.type = type; o.frequency.setValueAtTime(freq, t);
  if (freqEnd) o.frequency.exponentialRampToValueAtTime(freqEnd, t + dur);
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.start(t); o.stop(t + dur + 0.01);
}
const SFX = {
  flap() { osc("sine", 540, 0.18, 0.09, 370); },
  score() { const n = getAC().currentTime; [[660,0],[880,.07],[1100,.14]].forEach(([f,d]) => osc("square",f,.09,.11,null,n+d)); },
  hit() { osc("sawtooth",220,.28,.18,75); setTimeout(()=>osc("sine",110,.15,.12,60),60); },
};

// Day/night sky palettes — cycles every 10 points
// Each entry: [top, mid, bottom] gradient stops
const SKY_CYCLES=[
  ["#3a8abf","#87CEEB","#B3E5FC"], // day (0–9)
  ["#1a3a6b","#2d5a8e","#7ba7c7"], // late afternoon (10–19)
  ["#0f1d3d","#1e3a5f","#4a6d8c"], // dusk (20–29)
  ["#050b1a","#0d1f3c","#1a2d4a"], // night (30–39)
  ["#1a0a2e","#2d1b4e","#4a2d6b"], // deep night (40–49)
  ["#2d1a0a","#5c3a1a","#8c6a3a"], // dawn (50–59)
  ["#5c2a0a","#a05020","#d4844a"], // sunrise (60–69)
  ["#3a8abf","#87CEEB","#B3E5FC"], // back to day (70+)
];

function makeClouds(W, H) {
  return Array.from({length:5},(_,i)=>({
    x:(W/5)*i+Math.random()*30, y:20+Math.random()*H*0.22,
    w:36+Math.random()*50, spd:0.15+Math.random()*0.25, layer:i%2,
  }));
}
function makeState(W, H) {
  return {
    W, H, phase:"menu", frame:0, score:0,
    hiScore:+(localStorage.getItem("pt_hi")||0),
    leaderboard:JSON.parse(localStorage.getItem("pt_lb")||"[]"),
    bird:{x:W*0.22, y:H/2, vy:0, rot:0, sq:1, st:1, wf:0, blink:90, blinkOpen:true},
    pipes:[], clouds:makeClouds(W,H), particles:[], floats:[],
    gx:0, shX:0, shY:0, shMag:0, dt:0, pTimer:0, menuBob:0,
    skyT:0,        // 0-1 interpolation progress toward next sky phase
    skyPhase:0,    // which SKY_CYCLES index we are currently on
    lastSkyScore:0,// score at which we last triggered a sky shift
    // Stars: fixed positions seeded once, never move (parallax-free for pixel art feel)
    stars:(()=>{
      const rng=(seed)=>{let s=seed;return()=>{s=(s*1664525+1013904223)&0xffffffff;return(s>>>0)/0xffffffff;};};
      const r=rng(42);
      return Array.from({length:55},()=>({
        x:r(), y:r()*0.78,          // x/y as 0-1 fractions of canvas size
        r:r()<0.15?1.5:r()<0.6?1:0.7, // mix of sizes
        twinkle:r()*Math.PI*2,       // phase offset for twinkle animation
        twinkleSpd:0.04+r()*0.04,    // each star twinkles at a different rate
      }));
    })(),
  };
}
function wingF(f) { return Math.floor(f/6)%3; }
function pipeSpd(s) { return 2.2+s*0.04; }
function makePipe(s,W,H) {
  const gap = Math.max(108,152-s*1.8);
  const topH = 50+Math.random()*(H-GH-100-gap);
  return {x:W+16, topH, gap, passed:false};
}
function tickParticles(gs) {
  gs.particles.forEach(p=>{p.x+=p.vx;p.y+=p.vy;p.vy+=0.13;p.life-=0.032;});
  gs.particles=gs.particles.filter(p=>p.life>0);
}
function doFlap(gs) {
  if(gs.phase==="dead") return;
  if(gs.phase==="menu"){gs.phase="ready";return;}
  if(gs.phase==="ready") gs.phase="play";
  if(gs.phase!=="play") return;
  gs.bird.vy=-4.3; gs.bird.sq=0.68; gs.bird.st=1.32;
  SFX.flap();
}
function updateGame(gs) {
  const {W,H}=gs;
  gs.frame++; gs.menuBob=Math.sin(gs.frame*0.055)*5;
  gs.clouds.forEach(c=>{c.x-=c.spd;if(c.x+c.w<0){c.x=W+8;c.y=20+Math.random()*H*0.22;c.w=36+Math.random()*50;}});
  const gspd=(gs.phase==="play"||gs.phase==="dying")?pipeSpd(gs.score):0.5;
  gs.gx=(gs.gx-gspd)%40;
  if(gs.phase==="menu"||gs.phase==="ready"){
    gs.bird.y=H/2+gs.menuBob; gs.bird.rot=0; gs.bird.wf=wingF(gs.frame); return;
  }
  if(gs.phase==="dying"){
    gs.dt++; gs.bird.vy+=0.46*1.6; gs.bird.y+=gs.bird.vy;
    gs.bird.rot=Math.min(90,gs.bird.rot+9);
    gs.shMag*=0.78; gs.shX=(Math.random()-0.5)*gs.shMag; gs.shY=(Math.random()-0.5)*gs.shMag;
    tickParticles(gs); if(gs.dt>55) gs.phase="dead"; return;
  }
  if(gs.phase==="dead") return;
  gs.bird.vy+=0.12; gs.bird.y+=gs.bird.vy;
  const tRot=Math.min(90,Math.max(-22,gs.bird.vy*5.2));
  gs.bird.rot+=(tRot-gs.bird.rot)*0.14;
  gs.bird.sq+=(1-gs.bird.sq)*0.22; gs.bird.st+=(1-gs.bird.st)*0.22;
  gs.bird.wf=wingF(gs.frame);
  gs.bird.blink--;
  if(gs.bird.blink<=0){gs.bird.blinkOpen=!gs.bird.blinkOpen;gs.bird.blink=gs.bird.blinkOpen?80+Math.random()*120:7;}
  gs.shMag*=0.82; gs.shX=(Math.random()-0.5)*gs.shMag; gs.shY=(Math.random()-0.5)*gs.shMag;
  // Smooth sky transition (takes ~60 frames = ~1 second)
  if(gs.skyT<1) gs.skyT=Math.min(1,gs.skyT+0.016);
  gs.pTimer++;
  const spawnEvery=Math.max(82,138-gs.score*2);
  if(gs.pTimer>=spawnEvery){gs.pTimer=0;gs.pipes.push(makePipe(gs.score,W,H));}
  const spd=pipeSpd(gs.score);
  gs.pipes.forEach(p=>p.x-=spd);
  gs.pipes=gs.pipes.filter(p=>p.x>-PW-20);
  gs.pipes.forEach(p=>{
    if(!p.passed&&p.x+PW<gs.bird.x){
      p.passed=true; gs.score++;
      if(gs.score>gs.hiScore){gs.hiScore=gs.score;localStorage.setItem("pt_hi",gs.hiScore);}
      SFX.score();
      gs.floats.push({x:gs.bird.x+20,y:gs.bird.y-22,life:1});
      // Trigger sky shift every 10 points
      if(gs.score%10===0){
        gs.skyPhase=(gs.skyPhase+1)%SKY_CYCLES.length;
        gs.skyT=0; // start smooth transition
      }
    }
  });
  gs.floats.forEach(f=>{f.y-=1.4;f.life-=0.028;});
  gs.floats=gs.floats.filter(f=>f.life>0);
  tickParticles(gs);
  const bx=gs.bird.x-11,by=gs.bird.y-11,bw=22,bh=22;
  if(gs.bird.y>=H-GH-14||gs.bird.y<=12){die(gs);return;}
  for(const p of gs.pipes){
    if(bx+bw>p.x+4&&bx<p.x+PW-4){if(by<p.topH||by+bh>p.topH+p.gap){die(gs);return;}}
  }
}
function die(gs) {
  if(gs.phase!=="play") return;
  SFX.hit(); gs.phase="dying"; gs.dt=0; gs.shMag=11;
  for(let i=0;i<14;i++){
    const a=Math.random()*Math.PI*2,s=1.2+Math.random()*3.2;
    gs.particles.push({x:gs.bird.x,y:gs.bird.y,vx:Math.cos(a)*s,vy:Math.sin(a)*s-1,life:1,color:[C.pA,C.pB,"#fff"][i%3],size:2+Math.random()*4});
  }
  gs.leaderboard.push({score:gs.score,date:Date.now()});
  gs.leaderboard.sort((a,b)=>b.score-a.score);
  gs.leaderboard=gs.leaderboard.slice(0,5);
  localStorage.setItem("pt_lb",JSON.stringify(gs.leaderboard));
}

// ─────────────────────────────────────────────────────────────────────────────
// CANVAS DRAW HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function rrect(ctx,x,y,w,h,r){
  ctx.beginPath();
  if(ctx.roundRect){ctx.roundRect(x,y,w,h,r);}
  else{ctx.moveTo(x+r,y);ctx.lineTo(x+w-r,y);ctx.quadraticCurveTo(x+w,y,x+w,y+r);ctx.lineTo(x+w,y+h-r);ctx.quadraticCurveTo(x+w,y+h,x+w-r,y+h);ctx.lineTo(x+r,y+h);ctx.quadraticCurveTo(x,y+h,x,y+h-r);ctx.lineTo(x,y+r);ctx.quadraticCurveTo(x,y,x+r,y);ctx.closePath();}
}
function pxText(ctx,t,x,y,sz,col,align="center"){
  ctx.save();ctx.font=`900 ${sz}px 'Press Start 2P',monospace`;
  ctx.textAlign=align;ctx.textBaseline="middle";
  ctx.strokeStyle="rgba(0,0,0,0.7)";ctx.lineWidth=sz*0.19;ctx.lineJoin="round";
  ctx.strokeText(t,x,y);ctx.fillStyle=col;ctx.fillText(t,x,y);ctx.restore();
}
// Linear interpolation between two hex colours at a given stop ratio
// How visible stars are at each SKY_CYCLES phase index (0=hidden,1=full)
const STAR_ALPHA=[0,0.15,0.55,1,1,0.7,0.2,0];

function lerpHex(a,b,t){
  const p=(c)=>[parseInt(c.slice(1,3),16),parseInt(c.slice(3,5),16),parseInt(c.slice(5,7),16)];
  const [ar,ag,ab]=p(a),[br,bg,bb]=p(b);
  const r=Math.round(ar+(br-ar)*t),g=Math.round(ag+(bg-ag)*t),bl=Math.round(ab+(bb-ab)*t);
  return`#${r.toString(16).padStart(2,"0")}${g.toString(16).padStart(2,"0")}${bl.toString(16).padStart(2,"0")}`;
}
function drawSky(ctx,W,H,gs){
  // Determine current and next sky palette
  const cur=SKY_CYCLES[gs.skyPhase];
  const nxt=SKY_CYCLES[(gs.skyPhase+1)%SKY_CYCLES.length];
  const t=gs?gs.skyT:1;
  const top=lerpHex(cur[0],nxt[0],t);
  const mid=lerpHex(cur[1],nxt[1],t);
  const bot=lerpHex(cur[2],nxt[2],t);
  const g=ctx.createLinearGradient(0,0,0,H);
  g.addColorStop(0,top);g.addColorStop(0.5,mid);g.addColorStop(1,bot);
  ctx.fillStyle=g;ctx.fillRect(0,0,W,H);
}
function drawStars(ctx,W,H,gs){
  // Interpolate star opacity between current and next phase
  const cur=STAR_ALPHA[gs.skyPhase];
  const nxt=STAR_ALPHA[(gs.skyPhase+1)%STAR_ALPHA.length];
  const alpha=cur+(nxt-cur)*gs.skyT;
  if(alpha<=0) return;
  gs.stars.forEach(s=>{
    // Gentle twinkle: ±20% brightness variation
    const twinkle=0.8+0.2*Math.sin(gs.frame*s.twinkleSpd+s.twinkle);
    ctx.globalAlpha=alpha*twinkle;
    ctx.fillStyle="#ffffff";
    ctx.beginPath();
    ctx.arc(s.x*W, s.y*(H-GH), s.r, 0, Math.PI*2);
    ctx.fill();
    // Tiny 4-point cross flare on the larger stars
    if(s.r>=1.4){
      ctx.globalAlpha=alpha*twinkle*0.4;
      ctx.fillRect(s.x*W-3, s.y*(H-GH)-0.5, 6, 1);
      ctx.fillRect(s.x*W-0.5, s.y*(H-GH)-3, 1, 6);
    }
  });
  ctx.globalAlpha=1;
}
function drawCloud(ctx,x,y,w){
  const h=w*0.44;
  ctx.fillStyle=C.cloud;ctx.beginPath();
  [[0.18,0.70,0.42],[0.38,0.53,0.58],[0.62,0.38,0.68],[0.78,0.69,0.44],[0.50,0.76,0.50]].forEach(([lx,ly,lr])=>{
    const cx=x+w*lx,cy=y+h*ly,r=h*lr;
    ctx.moveTo(cx+r,cy);ctx.arc(cx,cy,r,0,Math.PI*2);
  });
  ctx.fill();
}
function drawPipe(ctx,px,topH,gap,H){
  const cx=px-5,cw=PW+10;
  function pg(x,w){const g=ctx.createLinearGradient(x,0,x+w,0);g.addColorStop(0,C.pD);g.addColorStop(0.22,C.pL);g.addColorStop(0.78,C.pM);g.addColorStop(1,C.pD);return g;}
  ctx.fillStyle=pg(px,PW);ctx.fillRect(px,0,PW,topH-PHC);
  ctx.fillStyle=pg(cx,cw);ctx.fillRect(cx,topH-PHC,cw,PHC);
  ctx.fillStyle="rgba(255,255,255,0.22)";ctx.fillRect(px+5,0,6,topH);ctx.fillRect(cx+4,topH-PHC,6,PHC);
  ctx.fillStyle="rgba(0,0,0,0.14)";ctx.fillRect(px+PW-9,0,9,topH);
  const bY=topH+gap;
  ctx.fillStyle=pg(px,PW);ctx.fillRect(px,bY+PHC,PW,H-bY-PHC);
  ctx.fillStyle=pg(cx,cw);ctx.fillRect(cx,bY,cw,PHC);
  ctx.fillStyle="rgba(255,255,255,0.22)";ctx.fillRect(px+5,bY+PHC,6,H-bY);ctx.fillRect(cx+4,bY,6,PHC);
  ctx.fillStyle="rgba(0,0,0,0.14)";ctx.fillRect(px+PW-9,bY,9,H-bY);
}
function drawGround(ctx,gx,W,H){
  const gY=H-GH;
  ctx.fillStyle=C.ground;ctx.fillRect(0,gY,W,GH);
  ctx.fillStyle=C.dirt;ctx.fillRect(0,gY+18,W,GH-18);
  for(let i=0;i<W+44;i+=22){ctx.fillStyle=C.groundS;ctx.fillRect(((i+gx)%W)-4,gY,11,18);}
  ctx.fillStyle="rgba(255,255,255,0.2)";ctx.fillRect(0,gY,W,2);
  ctx.fillStyle="rgba(0,0,0,0.2)";ctx.fillRect(0,gY+18,W,2);
}
function drawBird(ctx,b){
  ctx.save();ctx.translate(b.x|0,b.y|0);ctx.rotate(b.rot*Math.PI/180);ctx.scale(b.st,b.sq);
  const r=14;
  ctx.fillStyle=C.bB2;ctx.beginPath();rrect(ctx,-r,-r,r*2,r*2,7);ctx.fill();
  ctx.fillStyle=C.bB1;ctx.beginPath();rrect(ctx,-r+2,-r+2,r*2-4,r*2-4,6);ctx.fill();
  ctx.fillStyle="rgba(255,255,255,0.35)";ctx.beginPath();ctx.ellipse(-3,-4,6,4.5,-0.3,0,Math.PI*2);ctx.fill();
  const wy=[3,5,7];
  ctx.fillStyle=C.bW;ctx.beginPath();rrect(ctx,-r-1,wy[b.wf],10,7,3);ctx.fill();
  ctx.fillStyle="#fff";ctx.beginPath();ctx.arc(5,-4,5,0,Math.PI*2);ctx.fill();
  if(b.blinkOpen){
    ctx.fillStyle="#1a1a2e";ctx.beginPath();ctx.arc(6,-4,3,0,Math.PI*2);ctx.fill();
    ctx.fillStyle="#fff";ctx.beginPath();ctx.arc(7,-5.2,1,0,Math.PI*2);ctx.fill();
  }else{ctx.fillStyle=C.bB2;ctx.fillRect(1,-7,9,5);ctx.fillStyle="#1a1a2e";ctx.fillRect(2,-6,7,2);}
  ctx.fillStyle=C.bBk;ctx.beginPath();ctx.moveTo(r-1,-3.5);ctx.lineTo(r+9,0.5);ctx.lineTo(r-1,4.5);ctx.closePath();ctx.fill();
  ctx.restore();
}

function renderGame(ctx,gs){
  const {W,H}=gs;
  ctx.save();ctx.translate(gs.shX|0,gs.shY|0);
  drawSky(ctx,W,H,gs);
  drawStars(ctx,W,H,gs);
  // Clouds fade out as night comes in (inverse of star alpha)
  const _curA=STAR_ALPHA[gs.skyPhase];
  const _nxtA=STAR_ALPHA[(gs.skyPhase+1)%STAR_ALPHA.length];
  const _nightA=_curA+(_nxtA-_curA)*gs.skyT;
  const _cloudVis=Math.max(0,1-_nightA*1.4); // clouds gone before fully dark
  if(_cloudVis>0.01){
    gs.clouds.forEach(c=>{ctx.globalAlpha=(c.layer===0?0.52:0.9)*_cloudVis;drawCloud(ctx,c.x,c.y,c.w);ctx.globalAlpha=1;});
  }
  gs.pipes.forEach(p=>drawPipe(ctx,p.x,p.topH,p.gap,H));
  drawGround(ctx,gs.gx,W,H);
  gs.particles.forEach(p=>{ctx.globalAlpha=Math.max(0,p.life);ctx.fillStyle=p.color;ctx.beginPath();rrect(ctx,p.x-p.size/2,p.y-p.size/2,p.size,p.size,2);ctx.fill();ctx.globalAlpha=1;});
  if(gs.phase!=="dead") drawBird(ctx,gs.bird);
  gs.floats.forEach(f=>{ctx.globalAlpha=Math.max(0,f.life);pxText(ctx,"+1",f.x,f.y,16,C.pA);ctx.globalAlpha=1;});
  ctx.restore();

  // UI overlays
  if(gs.phase==="menu"){
    // Title
    pxText(ctx,"PECKER",W/2,H*0.07+24,W*0.068,T.yellow);
    pxText(ctx,"TAP",W/2,H*0.07+52,W*0.068,T.orange);

    // Best score display
    if(gs.hiScore>0){
      pxText(ctx,"BEST",W/2,H*0.25+12,W*0.032,"rgba(255,255,255,0.8)");
      pxText(ctx,`${gs.hiScore}`,W/2,H*0.25+26,W*0.042,T.yellow);
    }

    // TAP TO START pulse
    const pulse=0.5+0.5*Math.sin(gs.frame*0.1);
    ctx.globalAlpha=0.7+pulse*0.3;
    ctx.fillStyle="rgba(0,0,0,0.4)";rrect(ctx,W/2-88,H*0.38,176,38,9);ctx.fill();
    pxText(ctx,"TAP TO START",W/2,H*0.38+19,W*0.036,"#fff");
    ctx.globalAlpha=1;

    // ── Top Scores panel ──────────────────────────────────────────────────
    const lb=gs.leaderboard||[];
    const panelRows=Math.max(1,Math.min(lb.length,5));
    const rowH=18;
    const panelH=32+panelRows*rowH+6;
    const panelY=H*0.52;
    const panelW=Math.min(W-28,180);
    const panelX=W/2-panelW/2;

    ctx.fillStyle="rgba(0,0,0,0.45)";
    rrect(ctx,panelX,panelY,panelW,panelH,10);ctx.fill();
    ctx.strokeStyle="rgba(255,217,61,0.25)";ctx.lineWidth=1;
    rrect(ctx,panelX,panelY,panelW,panelH,10);ctx.stroke();

    pxText(ctx,"TOP SCORES",W/2,panelY+16,W*0.028,T.yellow);
    ctx.fillStyle="rgba(255,255,255,0.08)";
    ctx.fillRect(panelX+12,panelY+28,panelW-24,1);

    if(lb.length>0){
      const medals=["#FFD93D","#C0C0C0","#CD7F32","#aaaaaa","#aaaaaa"];
      lb.slice(0,5).forEach((entry,i)=>{
        const ry=panelY+32+i*rowH+rowH/2-2;
        pxText(ctx,`#${i+1}`,panelX+18,ry,W*0.025,medals[i]||"#aaa","center");
        pxText(ctx,`${entry.score}`,panelX+panelW-8,ry,W*0.025,"#fff","right");
      });
    }else if(gs.hiScore>0){
      pxText(ctx,`BEST  ${gs.hiScore}`,W/2,panelY+38+rowH/2-2,W*0.03,T.yellow);
    }else{
      pxText(ctx,"NO SCORES YET",W/2,panelY+38+rowH/2-2,W*0.022,"rgba(255,255,255,0.4)");
    }
  }
  if(gs.phase==="ready"){
    pxText(ctx,"GET READY!",W/2,80,W*0.036,"#fff");
  }
  if(["play","ready","dying","dead"].includes(gs.phase)){
    ctx.fillStyle="rgba(0,0,0,0.3)";rrect(ctx,W/2-32,8,64,36,8);ctx.fill();
    pxText(ctx,`${gs.score}`,W/2,26,22,"#fff");
  }
  if((gs.phase==="dying"&&gs.dt>20)||gs.phase==="dead"){
    ctx.fillStyle="rgba(0,0,0,0.5)";ctx.fillRect(0,0,W,H);
    ctx.fillStyle="rgba(15,15,40,0.92)";rrect(ctx,W/2-100,H*0.2,200,200,14);ctx.fill();
    ctx.strokeStyle=T.yellow;ctx.lineWidth=2.5;rrect(ctx,W/2-100,H*0.2,200,200,14);ctx.stroke();
    pxText(ctx,"GAME OVER",W/2,H*0.2+30,W*0.052,"#FF6B6B");
    ctx.fillStyle="rgba(255,255,255,0.07)";ctx.fillRect(W/2-80,H*0.2+48,160,1);
    pxText(ctx,"SCORE",W/2-46,H*0.2+64,W*0.024,"#999");
    pxText(ctx,"BEST",W/2+50,H*0.2+64,W*0.024,"#999");
    pxText(ctx,`${gs.score}`,W/2-46,H*0.2+92,24,"#fff");
    const isHi=gs.score>0&&gs.score>=gs.hiScore;
    pxText(ctx,`${gs.hiScore}`,W/2+50,H*0.2+92,24,isHi?T.yellow:"#fff");
    if(isHi){const p2=0.6+0.4*Math.sin(gs.frame*0.22);ctx.globalAlpha=p2;pxText(ctx,"NEW!",W/2+50,H*0.2+110,W*0.022,T.yellow);ctx.globalAlpha=1;}
    ctx.fillStyle="rgba(255,255,255,0.07)";ctx.fillRect(W/2-80,H*0.2+125,160,1);
    if(gs.phase==="dead"){
      const p3=0.82+0.18*Math.sin(gs.frame*0.13);
      ctx.globalAlpha=p3;ctx.fillStyle="#4AA028";rrect(ctx,W/2-70,H*0.2+148,140,40,10);ctx.fill();
      ctx.strokeStyle="#70D44A";ctx.lineWidth=2;rrect(ctx,W/2-70,H*0.2+148,140,40,10);ctx.stroke();
      ctx.globalAlpha=1;pxText(ctx,"PLAY AGAIN",W/2,H*0.2+168,W*0.032,"#fff");
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// useGame HOOK
// ─────────────────────────────────────────────────────────────────────────────
function useGame(canvasRef, W, H, interactive=true){ // eslint-disable-line no-unused-vars
  const gsRef=useRef(null);
  const touchTimeoutRef=useRef(null);
  // Tracks whether this canvas was the last one the user clicked/tapped,
  // so Space/ArrowUp fires on the right instance when multiple canvases exist.
  const activeRef=useRef(false);

  useEffect(()=>{
    const canvas=canvasRef.current; if(!canvas) return;
    const dpr=window.devicePixelRatio||1;
    canvas.width=W*dpr; canvas.height=H*dpr;
    canvas.style.width=W+"px"; canvas.style.height=H+"px";
    const ctx=canvas.getContext("2d"); ctx.scale(dpr,dpr);
    gsRef.current=makeState(W,H);
    let raf;
    const loop=()=>{updateGame(gsRef.current);renderGame(ctx,gsRef.current);raf=requestAnimationFrame(loop);};
    raf=requestAnimationFrame(loop);
    return ()=>cancelAnimationFrame(raf);
  },[canvasRef,W,H]);

  const handleTap=useCallback((cx,cy)=>{
    if(!interactive) return;
    const gs=gsRef.current; if(!gs) return;
    getAC();
    // Mark this canvas as the keyboard-active instance
    activeRef.current=true;
    const {W,H}=gs;
    if(gs.phase==="dead"){
      if(cx>W/2-70&&cx<W/2+70&&cy>H*0.2+148&&cy<H*0.2+188){
        gsRef.current=makeState(W,H);
        gsRef.current.phase="menu";
        gsRef.current.hiScore=+(localStorage.getItem("pt_hi")||0);
        gsRef.current.leaderboard=JSON.parse(localStorage.getItem("pt_lb")||"[]");
      }
      return;
    }
    doFlap(gs);
  },[interactive]);

  // ── Keyboard: Space / ArrowUp / W ────────────────────────────────────────
  useEffect(()=>{
    if(!interactive) return;
    const onKey=(e)=>{
      if(!activeRef.current) return;
      if(!["Space","ArrowUp","KeyW"].includes(e.code)) return;
      // Don't fire if the game modal is open (it has its own handler)
      if(document.querySelector('[data-modal="game"][data-open="true"]')) return;
      e.preventDefault(); // stop page scroll on Space/ArrowUp
      const gs=gsRef.current; if(!gs) return;
      getAC();
      if(gs.phase==="dead"){
        gsRef.current=makeState(gs.W,gs.H);
        gsRef.current.phase="menu";
        gsRef.current.hiScore=+(localStorage.getItem("pt_hi")||0);
        gsRef.current.leaderboard=JSON.parse(localStorage.getItem("pt_lb")||"[]");
        return;
      }
      doFlap(gs);
    };
    window.addEventListener("keydown",onKey);
    return ()=>window.removeEventListener("keydown",onKey);
  },[interactive]);

  const onCanvasClick=useCallback((e)=>{
    const r=canvasRef.current?.getBoundingClientRect(); if(!r) return;
    handleTap((e.clientX-r.left)*(W/r.width),(e.clientY-r.top)*(H/r.height));
  },[canvasRef,W,H,handleTap]);

  const onCanvasTouch=useCallback((e)=>{
    e.preventDefault();
    if(touchTimeoutRef.current) return;
    touchTimeoutRef.current=setTimeout(()=>{touchTimeoutRef.current=null;},20);
    const r=canvasRef.current?.getBoundingClientRect(); if(!r) return;
    const t=e.touches[0];
    handleTap((t.clientX-r.left)*(W/r.width),(t.clientY-r.top)*(H/r.height));
  },[canvasRef,W,H,handleTap]);

  return {onCanvasClick,onCanvasTouch};
}

// ─────────────────────────────────────────────────────────────────────────────
// GAME CANVAS COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function GameCanvas({width,height,interactive=true,borderRadius=12,style={}}){
  return <PeckerTapGame width={width} height={height} interactive={interactive} borderRadius={borderRadius} style={style}/>;
}


// ─────────────────────────────────────────────────────────────────────────────
// HAMBURGER ICON
// ─────────────────────────────────────────────────────────────────────────────
function HamburgerIcon({open}){
  const bar={
    display:"block", width:22, height:2, background:T.yellow,
    borderRadius:2, transition:"transform 0.25s ease, opacity 0.25s ease",
  };
  return(
    <div style={{width:22,height:16,display:"flex",flexDirection:"column",justifyContent:"space-between",cursor:"pointer"}}>
      <span style={{...bar,transform:open?"translateY(7px) rotate(45deg)":"none"}}/>
      <span style={{...bar,opacity:open?0:1,transform:open?"scaleX(0)":"none"}}/>
      <span style={{...bar,transform:open?"translateY(-7px) rotate(-45deg)":"none"}}/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE MENU DRAWER
// ─────────────────────────────────────────────────────────────────────────────
function MobileMenu({open,onClose,onPlay,dmgUrl}){
  return(
    <div style={{
      position:"fixed", top:64, left:0, right:0, zIndex:99,
      background:"rgba(13,15,26,0.98)", backdropFilter:"blur(20px)",
      borderBottom:`1px solid rgba(255,217,61,0.15)`,
      transform:open?"translateY(0)":"translateY(-110%)",
      transition:"transform 0.32s cubic-bezier(0.4,0,0.2,1)",
      padding:"8px 0 24px",
    }}>
      {[
        {label:"Features",href:"#features"},
        {label:"How to Play",href:"#howtoplay"},
      ].map(({label,href})=>(
        <a key={label} href={href} onClick={onClose} style={{
          display:"block", padding:"18px 28px",
          fontFamily:T.pixel, fontSize:13, color:"rgba(255,255,255,0.75)",
          textDecoration:"none", letterSpacing:"0.06em",
          borderBottom:"1px solid rgba(255,255,255,0.05)",
          transition:"color 0.2s, background 0.2s",
        }}
          onMouseEnter={e=>{e.currentTarget.style.color=T.yellow;e.currentTarget.style.background="rgba(255,217,61,0.05)";}}
          onMouseLeave={e=>{e.currentTarget.style.color="rgba(255,255,255,0.75)";e.currentTarget.style.background="transparent";}}
        >{label}</a>
      ))}
      <div style={{padding:"20px 28px 4px",display:"flex",flexDirection:"column",gap:12}}>
        <a href="https://buy.stripe.com/cNieVe7Qm7KMfc8fpl04804" onClick={onClose} style={{
          fontFamily:T.pixel, fontSize:11, color:T.yellow,
          padding:"13px 20px", borderRadius:10,
          border:`1px solid rgba(255,217,61,0.3)`,
          background:"rgba(255,217,61,0.06)",
          textDecoration:"none", textAlign:"center", letterSpacing:"0.04em",
          display:"block",
        }}>DOWNLOAD</a>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GAME MODAL
// ─────────────────────────────────────────────────────────────────────────────
function GameModal({open,onClose}){
  const overlayRef=useRef(null);
  const [canvasW,setCanvasW]=useState(360);
  const [canvasH,setCanvasH]=useState(540);

  useEffect(()=>{
    function measure(){
      const vw=window.innerWidth, vh=window.innerHeight;
      // Modal game is smaller than the hero canvas (hero maxes at 240×360)
      // Target ~360×540, constrained to viewport with generous padding
      const maxW=Math.min(360,vw-40);
      const maxH=Math.min(540,vh-100);
      // Maintain 2:3 ratio
      const byW=maxW; const byH=byW*1.5;
      if(byH<=maxH){setCanvasW(Math.floor(byW));setCanvasH(Math.floor(byH));}
      else{const h=Math.floor(maxH);setCanvasH(h);setCanvasW(Math.floor(h/1.5));}
    }
    measure();
    window.addEventListener("resize",measure);
    return ()=>window.removeEventListener("resize",measure);
  },[]);

  useEffect(()=>{
    const handler=(e)=>{
      if(!open) return;
      if(["Space","ArrowUp","KeyW"].includes(e.code)){
        e.preventDefault();
        const canvas=overlayRef.current?.querySelector("canvas");
        if(canvas){const r=canvas.getBoundingClientRect();canvas.dispatchEvent(new MouseEvent("click",{clientX:r.left+r.width/2,clientY:r.top+r.height/2,bubbles:true}));}
      }
      if(e.code==="Escape") onClose();
    };
    window.addEventListener("keydown",handler);
    return ()=>window.removeEventListener("keydown",handler);
  },[open,onClose]);

  return(
    <div
      ref={overlayRef}
      data-modal="game"
      data-open={open?"true":"false"}
      onClick={(e)=>{if(e.target===overlayRef.current)onClose();}}
      style={{
        position:"fixed",inset:0,zIndex:999,
        background:"rgba(5,7,18,0.96)",backdropFilter:"blur(10px)",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        opacity:open?1:0,pointerEvents:open?"all":"none",
        transition:"opacity 0.25s ease",
        padding:16,
      }}
    >
      <button
        onClick={onClose}
        style={{
          position:"absolute",top:20,right:20,
          fontFamily:T.pixel,fontSize:16,background:T.yellow,color:T.night,
          padding:"6px 8px",borderRadius:6,border:"none",cursor:"pointer",
          transition:"transform 0.15s,box-shadow 0.15s",
          WebkitTapHighlightColor:"transparent",
          zIndex:1000,
        }}
        onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.1)";}}
        onMouseLeave={e=>{e.currentTarget.style.transform="";}}
      >✕</button>
      <div style={{
        position:"relative",
        background:T.night2,
        border:`2px solid rgba(255,217,61,0.3)`,
        borderRadius:20,padding:8,
        boxShadow:"0 40px 120px rgba(0,0,0,0.8)",
        transform:open?"scale(1) translateY(0)":"scale(0.92) translateY(20px)",
        transition:"transform 0.3s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
                <div style={{fontFamily:T.pixel,fontSize:8,color:T.yellow,textAlign:"center",marginBottom:8,letterSpacing:"0.08em",padding:"0 8px"}}>
          PECKER TAP DEMO
        </div>
        {open&&<GameCanvas width={canvasW} height={canvasH} borderRadius={12}/>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FEATURE CARD
// ─────────────────────────────────────────────────────────────────────────────
function FeatureCard({icon,name,desc}){
  const [hov,setHov]=useState(false);
  return(
    <div
      style={{
        background:hov?T.night3:T.night2,
        padding:"28px 24px",
        border:`1px solid ${hov?"rgba(255,217,61,0.18)":"rgba(255,255,255,0.06)"}`,
        borderRadius:4,
        transition:"background 0.2s,border-color 0.2s,transform 0.2s",
        transform:hov?"translateY(-4px)":"none",
        cursor:"default",position:"relative",overflow:"hidden",
        height:"100%",boxSizing:"border-box",
      }}
      onMouseEnter={()=>setHov(true)}
      onMouseLeave={()=>setHov(false)}
    >
      {hov&&<div style={{position:"absolute",top:0,left:0,right:0,height:2,background:`linear-gradient(90deg,${T.yellow},${T.orange})`}}/>}
      <span style={{fontSize:40,marginBottom:16,display:"block",color:T.yellow}}>{icon}</span>
      <div style={{fontFamily:T.pixel,fontSize:14,color:T.yellow,marginBottom:12,lineHeight:1.9,letterSpacing:"0.02em"}}>{name}</div>
      <p style={{fontFamily:T.pixel,fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:2.2,letterSpacing:"0.02em",margin:0}}>{desc}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SCROLL REVEAL
// ─────────────────────────────────────────────────────────────────────────────
function Reveal({children,delay=0}){
  const [vis,setVis]=useState(false);
  const ref=useRef(null);
  useEffect(()=>{
    const obs=new IntersectionObserver(([e])=>{if(e.isIntersecting){setVis(true);obs.disconnect();}},{threshold:0.1});
    if(ref.current) obs.observe(ref.current);
    return ()=>obs.disconnect();
  },[]);
  return(
    <div ref={ref} style={{opacity:vis?1:0,transform:vis?"translateY(0)":"translateY(24px)",transition:`opacity 0.55s ${delay}s ease,transform 0.55s ${delay}s ease`}}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useIsMobile HOOK — simple breakpoint detector
// ─────────────────────────────────────────────────────────────────────────────
function useIsMobile(bp=768){
  const [mob,setMob]=useState(()=>typeof window!=="undefined"&&window.innerWidth<=bp);
  useEffect(()=>{
    const fn=()=>setMob(window.innerWidth<=bp);
    window.addEventListener("resize",fn);
    return ()=>window.removeEventListener("resize",fn);
  },[bp]);
  return mob;
}

// ─────────────────────────────────────────────────────────────────────────────
// GAME CANVAS SIZE HOOK — responsive canvas dims
// ─────────────────────────────────────────────────────────────────────────────
function useHeroCanvasSize(){
  const [size,setSize]=useState({w:240,h:360});
  useEffect(()=>{
    function calc(){
      const vw=window.innerWidth;
      if(vw<=480){const w=Math.min(240,vw-64);setSize({w,h:Math.round(w*1.5)});}
      else if(vw<=768){setSize({w:220,h:330});}
      else{setSize({w:240,h:360});}
    }
    calc();
    window.addEventListener("resize",calc);
    return ()=>window.removeEventListener("resize",calc);
  },[]);
  return size;
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function PeckerTapLanding({dmgUrl="#"}){
  const [modalOpen,setModalOpen]=useState(false);
  const [menuOpen,setMenuOpen]=useState(false);
  const [navHover,setNavHover]=useState(null);
  const [btnHover,setBtnHover]=useState(null);
  const isMobile=useIsMobile();
  const heroSize=useHeroCanvasSize();

  // Always opens the in-page modal — never navigates away to App.js
  const openModal=()=>{if(_ac)_ac.resume();setModalOpen(true);setMenuOpen(false);};
  const closeModal=()=>setModalOpen(false);
  const handlePlayGame=openModal;

  // Close menu on scroll
  useEffect(()=>{
    const fn=()=>{if(menuOpen)setMenuOpen(false);};
    window.addEventListener("scroll",fn,{passive:true});
    return ()=>window.removeEventListener("scroll",fn);
  },[menuOpen]);

  // Lock body scroll when menu open on mobile
  useEffect(()=>{
    document.body.style.overflow=(menuOpen&&isMobile)?"hidden":"";
    return ()=>{document.body.style.overflow="";};
  },[menuOpen,isMobile]);

  const FEATURES=[
    {icon:<FaTwitter/>,name:"UNLOCKABLE BIRDS",desc:"Collect feathers to unlock yellow, pink, blue, green, purple, and black bird palettes."},
    {icon:<FaMusic/>,name:"DYNAMIC SOUNDTRACK",desc:"A layered retro theme reacts to score, weather, and night, with a real double-quack and arcade defeat jingle."},
    {icon:<FaChartBar/>,name:"LOCAL LEADERBOARD",desc:"Top 5 scores saved locally. Medals for milestones. Beat your best every session."},
    {icon:<FaBolt/>,name:"THREE-LIFE CYCLE",desc:"Three pixel hearts protect feather progress. Lose the final heart and the bird returns to yellow."},
    {icon:<FaMobileAlt/>,name:"LIVING SKY",desc:"Clouds, day-to-night color, breeze, rain, storms, and mist evolve around every run — never snow."},
    {icon:<FaBullseye/>,name:"GOLDEN FEATHERS",desc:"Detailed floating collectibles tempt you toward risky pipe edges without changing the tuned flight physics."},
  ];

  const keyPill={
    display:"inline-block",background:T.night3,
    border:"1px solid rgba(255,255,255,0.2)",borderBottom:"3px solid rgba(255,255,255,0.2)",
    borderRadius:6,padding:"3px 10px",fontFamily:T.pixel,fontSize:7,color:T.sky,margin:"0 2px",
  };

  const STEPS=[
    {num:"01",title:"CLICK THE MENU BAR ICON",body:<>The game pops up right below the tray. No dock. No app switcher. Instant access.</>},
    {num:"02",title:"TAP TO FLAP",body:<>Press <span style={keyPill}>SPACE</span> or <span style={keyPill}>TAP</span> to flap. Each tap fights gravity. Time it perfectly.</>},
    {num:"03",title:"DODGE THE PIPES",body:<>Green pipes scroll in from the right. The gap narrows as your score climbs.</>},
    {num:"04",title:"COLLECT AND SURVIVE",body:<>Golden feathers unlock new bird colors every 10. Your three hearts preserve progress until the final death.</>},
  ];

  // ── Nav link style helper
  const navLinkStyle=(lbl)=>({
    fontFamily:T.body,fontSize:16,fontWeight:500,
    padding:"8px 14px",borderRadius:8,textDecoration:"none",
    transition:"color 0.2s,background 0.2s",
    color:navHover===lbl?"#fff":T.muted,
    background:navHover===lbl?"rgba(255,255,255,0.07)":"transparent",
    WebkitTapHighlightColor:"transparent",
  });

  return(
    <div style={{background:T.night,color:"#fff",fontFamily:T.body,fontSize:16,lineHeight:1.7,overflowX:"hidden",minHeight:"100vh",position:"relative"}}>

      {/* ── GLOBAL CSS ── */}
      <style>{`
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        body{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;}
        ::-webkit-scrollbar{width:4px;}
        ::-webkit-scrollbar-track{background:#0D0F1A;}
        ::-webkit-scrollbar-thumb{background:#FFD93D;border-radius:2px;}
        @keyframes navBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
        @keyframes bigBob{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-14px) rotate(4deg)}}
        @keyframes scrollBob{0%,100%{transform:translateX(-50%) translateY(0)}50%{transform:translateX(-50%) translateY(6px)}}
        @keyframes shine{0%,60%{left:-100%}100%{left:200%}}
        @keyframes badgePulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.8)}}
        .pt-bob{animation:navBob 1.4s ease-in-out infinite}
        .pt-dl-bird{animation:bigBob 1.8s ease-in-out infinite;font-size:clamp(48px,8vw,72px);display:block;margin:0 auto 24px}
        .pt-scroll-hint{position:absolute;bottom:24px;left:50%;transform:translateX(-50%);z-index:2;display:flex;flex-direction:column;alignItems:center;gap:6px;font-size:11px;color:rgba(255,255,255,0.4);font-family:'Press Start 2P',monospace;animation:scrollBob 2s ease-in-out infinite;letter-spacing:.1em;}
        /* Touch targets >= 44px */
        @media(max-width:768px){
          .pt-nav-link-desktop{display:none!important;}
          a,button{-webkit-tap-highlight-color:transparent;}
          .features-grid{grid-template-columns:1fr!important;}
        }
        @media(min-width:769px) and (max-width:1024px){
          .features-grid{grid-template-columns:repeat(2,1fr)!important;}
        }
        @media(min-width:1025px){
          .features-grid{grid-template-columns:repeat(3,1fr)!important;}
          .pt-hamburger{display:none!important;}
        }
      `}</style>

      {/* NAVBAR */}
      <nav style={{
        position:"fixed",top:0,left:0,right:0,zIndex:100,
        display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"0 clamp(16px,4vw,40px)",height:64,
        background:"rgba(13,15,26,0.92)",backdropFilter:"blur(16px)",
        borderBottom:"1px solid rgba(255,217,61,0.12)",
      }}>
        {/* Logo */}
        <div style={{fontFamily:"'Press Start 2P', monospace",fontSize:clamp(10,13),color:"#FFD93D",display:"flex",alignItems:"center",gap:10,textDecoration:"none",flexShrink:0,cursor:"default"}}>
          <span style={{fontFamily:"'Press Start 2P', monospace !important",fontSize:"clamp(11px,3vw,15px) !important",color:"#FFD93D !important"}}>PeckerTap</span>
        </div>

        {/* Desktop links */}
        <div className="pt-nav-link-desktop" style={{display:"flex",alignItems:"center",gap:6}}>
          {["Features","How to Play","Download"].map(lbl=>(
            <a key={lbl} href={`#${lbl.toLowerCase().replace(/ /g,"")}`}
              style={navLinkStyle(lbl)}
              onMouseEnter={()=>setNavHover(lbl)} onMouseLeave={()=>setNavHover(null)}
            >{lbl}</a>
          ))}
        </div>

        {/* Hamburger */}
        <button
          className="pt-hamburger"
          onClick={()=>setMenuOpen(o=>!o)}
          aria-label={menuOpen?"Close menu":"Open menu"}
          aria-expanded={menuOpen}
          style={{
            background:"none",border:"none",cursor:"pointer",
            padding:"12px 8px",display:"flex",alignItems:"center",justifyContent:"center",
            minWidth:44,minHeight:44,WebkitTapHighlightColor:"transparent",
          }}
        >
          <HamburgerIcon open={menuOpen}/>
        </button>
      </nav>

      {/* Mobile slide-down menu */}
      <MobileMenu open={menuOpen} onClose={()=>setMenuOpen(false)} onPlay={handlePlayGame} dmgUrl={dmgUrl}/>

      {/* Backdrop for mobile menu */}
      {menuOpen&&(
        <div
          onClick={()=>setMenuOpen(false)}
          style={{position:"fixed",inset:0,zIndex:98,background:"rgba(0,0,0,0.4)"}}
        />
      )}

      {/* ── HERO ── */}
      <section id="hero" style={{
        position:"relative",minHeight:"100svh",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
        overflow:"hidden",padding:"80px clamp(16px,5vw,24px) clamp(40px,6vw,60px)",
        textAlign:"center",gap:0,
      }}>
        {/* Bg gradient */}
        <div style={{position:"absolute",inset:0,background:"linear-gradient(180deg,#1a3a5c 0%,#0D2440 40%,#0D0F1A 100%)",zIndex:0}}/>
        {/* Stars */}
        <div style={{position:"absolute",inset:0,zIndex:0,backgroundImage:"radial-gradient(1px 1px at 10% 15%,rgba(255,255,255,.8),transparent),radial-gradient(1px 1px at 25% 8%,rgba(255,255,255,.6),transparent),radial-gradient(1px 1px at 40% 22%,rgba(255,255,255,.9),transparent),radial-gradient(1px 1px at 55% 5%,rgba(255,255,255,.7),transparent),radial-gradient(1px 1px at 70% 18%,rgba(255,255,255,.5),transparent),radial-gradient(1px 1px at 85% 10%,rgba(255,255,255,.8),transparent),radial-gradient(2px 2px at 60% 12%,rgba(255,217,61,.6),transparent),radial-gradient(2px 2px at 80% 6%,rgba(135,206,235,.5),transparent)"}}/>

        {/* Content */}
        <div style={{position:"relative",zIndex:2,maxWidth:780,width:"100%"}}>
          {/* Badge */}
          <div style={{
            display:"inline-flex",alignItems:"center",gap:10,
            background:"rgba(255,217,61,0.12)",border:"1px solid rgba(255,217,61,0.35)",
            borderRadius:100,padding:"8px clamp(12px,4vw,20px)",marginBottom:clamp(20,36),
            fontFamily:T.pixel,fontSize:"clamp(12px,3vw,14px)",color:T.yellow,letterSpacing:"0.1em",
            boxShadow:"0 0 24px rgba(255,217,61,0.1)",
          }}>
            <span className="pt-badge-dot"/>
            NOW AVAILABLE FOR THE MACOS MENU BAR
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily:T.pixel,
            fontSize:"clamp(32px,8vw,64px)",color:T.yellow,
            textShadow:`4px 4px 0 ${T.orange},8px 8px 0 rgba(255,107,53,.35),0 0 60px rgba(255,217,61,.2)`,
            marginBottom:10,letterSpacing:3,
          }}>
            Pecker<br/>
            <span style={{color:"#fff",textShadow:"4px 4px 0 rgba(0,0,0,0.5),8px 8px 0 rgba(0,0,0,0.3)"}}>Tap</span>
          </h1>

          {/* Sub */}
          <p style={{
            fontFamily:T.pixel,
            fontSize:"clamp(12px,2.5vw,15px)",
            color:"rgba(255,255,255,0.6)",
            lineHeight:2.3,maxWidth:480,margin:"0 auto clamp(28px,5vw,52px)",
            letterSpacing:"0.05em",
          }}>
            The addictive tiny cube bird game that lives right in your macOS menu bar.
            One tap. Infinite fun. Always ready.
          </p>

          {/* CTAs */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:12,flexWrap:"wrap"}}>
            <a href="https://buy.stripe.com/cNieVe7Qm7KMfc8fpl04804"
              style={{
                display:"inline-flex",alignItems:"center",gap:10,
                background:T.yellow,color:T.night,fontFamily:T.pixel,
                fontSize:"clamp(11px,3vw,13px)",
                padding:"clamp(14px,3vw,18px) clamp(20px,4vw,28px)",
                borderRadius:12,border:"none",cursor:"pointer",
                boxShadow:btnHover==="dl"?`0 8px 0 ${T.orange},0 20px 60px rgba(255,217,61,.4)`:`0 4px 0 ${T.orange},0 12px 40px rgba(255,217,61,.3)`,
                textDecoration:"none",transition:"transform 0.15s,box-shadow 0.2s",
                transform:btnHover==="dl"?"translateY(-3px)":"none",
                WebkitTapHighlightColor:"transparent",letterSpacing:"0.03em",
              }}
              onMouseEnter={()=>setBtnHover("dl")} onMouseLeave={()=>setBtnHover(null)}
            >
              <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M9 2v10M5 8l4 4 4-4M3 14h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              DOWNLOAD 
            </a>
          </div>
        </div>

        {/* Hero game canvas */}
        <div style={{position:"relative",zIndex:2,marginTop:"clamp(36px,6vw,60px)"}}>
          <div style={{
            position:"relative",display:"inline-block",
            background:"rgba(13,15,26,0.8)",
            border:"2px solid rgba(255,217,61,0.25)",
            borderRadius:22,padding:8,
            boxShadow:"0 30px 80px rgba(0,0,0,0.7),0 0 0 1px rgba(255,255,255,0.04),0 0 60px rgba(255,217,61,0.08)",
          }}>
            <div style={{position:"absolute",inset:-2,borderRadius:24,background:"linear-gradient(135deg,rgba(255,217,61,0.15),transparent 60%,rgba(135,206,235,0.1))",pointerEvents:"none"}}/>
            <GameCanvas width={heroSize.w} height={heroSize.h} borderRadius={14}/>
          </div>
          <div style={{marginTop:14,fontFamily:T.pixel,fontSize:"clamp(10px,2.5vw,12px)",color:"rgba(255,255,255,0.35)",letterSpacing:"0.12em",textAlign:"center"}}>
             PLAYABLE DEMO - TAP TO FLAP
          </div>
        </div>

              </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{padding:"clamp(60px,10vw,100px) clamp(16px,5vw,40px)"}}>
        <div style={{maxWidth:1100,margin:"0 auto"}}>
          <Reveal><div style={{fontFamily:T.pixel,fontSize:11,color:T.orange,letterSpacing:"0.18em",marginBottom:18}}>FEATURES</div></Reveal>
          <Reveal delay={0.05}><h2 style={{fontFamily:T.pixel,fontSize:"clamp(16px,3.5vw,26px)",color:"#fff",lineHeight:1.7,marginBottom:20,letterSpacing:1}}>EVERYTHING YOU<br/>DIDN'T KNOW<br className="mobile-br"/>YOU NEEDED</h2></Reveal>
          <Reveal delay={0.1}><p style={{fontFamily:T.pixel,fontSize:"clamp(11px,2.5vw,14px)",color:"rgba(255,255,255,0.5)",maxWidth:480,lineHeight:2.2,letterSpacing:"0.04em",marginBottom:0}}>Crafted with way too much attention to game feel. Every pixel, every sound, every bounce — dangerously addictive (you’ve been warned).</p></Reveal>
          <div className="features-grid" style={{
            display:"grid",
            gridTemplateColumns:"repeat(3,1fr)",
            gap:4,marginTop:"clamp(32px,5vw,60px)",
            maxWidth:"1200px",
            margin:"0 auto",
          }}>
            {FEATURES.map((f,i)=>(
              <Reveal key={f.name} delay={i*0.06}>
                <FeatureCard {...f}/>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW TO PLAY ── */}
      <section id="howtoplay" style={{padding:"clamp(60px,10vw,100px) clamp(16px,5vw,40px)",background:T.night2}}>
        <div style={{
          maxWidth:1100,margin:"0 auto",
          display:"grid",
          gridTemplateColumns:isMobile?"1fr":"1fr 1fr",
          gap:isMobile?"40px":"80px",
          alignItems:"center",
        }}>
          {/* Steps */}
          <div>
            <Reveal><div style={{fontFamily:T.pixel,fontSize:12,color:T.orange,letterSpacing:"0.18em",marginBottom:18}}>HOW TO PLAY</div></Reveal>
            <Reveal delay={0.05}><h2 style={{fontFamily:T.pixel,fontSize:"clamp(18px,4.5vw,30px)",color:"#fff",lineHeight:1.7,marginBottom:0,letterSpacing:1}}>SIMPLE TO LEARN.<br/>IMPOSSIBLE TO MASTER.</h2></Reveal>
            <div style={{marginTop:36,display:"flex",flexDirection:"column"}}>
              {STEPS.map((s,i)=>(
                <Reveal key={s.num} delay={i*0.08}>
                  <div style={{
                    display:"flex",gap:20,alignItems:"flex-start",
                    padding:"clamp(18px,3vw,28px) 0",
                    borderBottom:i<STEPS.length-1?"1px solid rgba(255,255,255,0.06)":"none",
                  }}>
                    <div style={{
                      fontFamily:T.pixel,fontSize:13,color:T.night,background:T.yellow,
                      width:44,height:44,borderRadius:8,display:"flex",alignItems:"center",justifyContent:"center",
                      flexShrink:0,marginTop:2,boxShadow:`0 3px 0 ${T.orange}`,
                    }}>{s.num}</div>
                    <div>
                      <strong style={{display:"block",fontFamily:T.pixel,fontSize:"clamp(12px,2.5vw,14px)",color:"#fff",marginBottom:8,lineHeight:1.9}}>{s.title}</strong>
                      <p style={{fontFamily:T.pixel,fontSize:"clamp(10px,2vw,11px)",color:"rgba(255,255,255,0.5)",lineHeight:2.3,letterSpacing:"0.02em",margin:0}}>{s.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Preview canvas ? hidden on small mobile, shown on tablet+ */}
          {!isMobile&&(
            <Reveal delay={0.15}>
              <div style={{display:"flex",justifyContent:"center"}}>
                <div style={{
                  background:T.night,border:"2px solid rgba(255,217,61,0.25)",
                  borderRadius:20,padding:8,boxShadow:"0 40px 100px rgba(0,0,0,0.7)",
                  display:"inline-block",position:"relative",
                }}>
                  <GameCanvas width={240} height={360} borderRadius={12}/>
                  <div style={{position:"absolute",bottom:-22,left:"50%",transform:"translateX(-50%)",fontFamily:T.pixel,fontSize:7,color:T.muted,whiteSpace:"nowrap",letterSpacing:"0.1em"}}>
                    TRY IT - CLICK TO FLAP
                  </div>
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ── DOWNLOAD ── */}
      <section id="download" style={{padding:"clamp(60px,10vw,100px) clamp(16px,5vw,40px)",textAlign:"center",position:"relative"}}>
        <div style={{position:"absolute",inset:0,background:"radial-gradient(ellipse 60% 50% at 50% 50%,rgba(255,217,61,0.06),transparent 70%)",pointerEvents:"none"}}/>
        <div style={{position:"relative",zIndex:1,maxWidth:700,margin:"0 auto"}}>
          <Reveal><div style={{fontFamily:T.pixel,fontSize:11,color:T.orange,letterSpacing:"0.18em",marginBottom:18}}>GET THE GAME</div></Reveal>
          <Reveal delay={0.05}><h2 style={{fontFamily:T.pixel,fontSize:"clamp(16px,4vw,28px)",color:T.yellow,marginBottom:24,lineHeight:1.7,letterSpacing:1}}>READY TO TAP?</h2></Reveal>
          <Reveal delay={0.1}><p style={{fontFamily:T.pixel,fontSize:"clamp(8px,2vw,11px)",color:"rgba(255,255,255,0.5)",marginBottom:"clamp(32px,5vw,48px)",lineHeight:2.3,letterSpacing:"0.03em"}}>Download the macOS app. Drop it in your Applications folder. That's it. Your menu bar will never be boring again.</p></Reveal>

          <Reveal delay={0.15}>
            <div style={{
              background:T.night2,border:"1px solid rgba(255,217,61,0.2)",
              borderRadius:"clamp(14px,3vw,20px)",padding:"clamp(24px,5vw,40px)",
              display:"flex",flexDirection:"column",alignItems:"center",gap:"clamp(16px,3vw,24px)",
            }}>
              {/* Platform badge
              <div style={{
                display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",justifyContent:"center",
                fontFamily:T.pixel,fontSize:"clamp(6px,1.4vw,7px)",color:"rgba(255,255,255,0.45)",
                background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                borderRadius:100,padding:"10px clamp(14px,3vw,22px)",letterSpacing:"0.05em",lineHeight:2,
                textAlign:"center",
              }}>
                <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M9 2C6.24 2 4 4.24 4 7v4.5a5 5 0 0010 0V7c0-2.76-2.24-5-5-5z" stroke="currentColor" strokeWidth="1.5" fill="none" opacity=".5"/><path d="M9 2C6.24 2 4 4.24 4 7v4.5a5 5 0 0010 0V7c0-2.76-2.24-5-5-5z" stroke="currentColor" strokeWidth="1.5" fill="none"/></svg>
                macOS 12+ · Apple Silicon &amp; Intel
              </div> */}

              {/* Download button */}
              <a href="https://buy.stripe.com/cNieVe7Qm7KMfc8fpl04804"
                className="pt-shine"
                style={{
                  display:"inline-flex",alignItems:"center",justifyContent:"center",gap:14,
                  background:T.yellow,color:T.night,fontFamily:T.pixel,
                  fontSize:"clamp(11px,3vw,13px)",
                  padding:"clamp(16px,3vw,22px) clamp(24px,5vw,40px)",
                  borderRadius:14,border:"none",cursor:"pointer",
                  boxShadow:btnHover==="dlbig"?`0 10px 0 ${T.orange},0 28px 70px rgba(255,217,61,.45)`:`0 6px 0 ${T.orange},0 20px 60px rgba(255,217,61,.35)`,
                  textDecoration:"none",transition:"transform 0.15s,box-shadow 0.2s",
                  transform:btnHover==="dlbig"?"translateY(-4px)":"none",
                  WebkitTapHighlightColor:"transparent",letterSpacing:"0.03em",
                  width:"100%",maxWidth:360,textAlign:"center",
                }}
                onMouseEnter={()=>setBtnHover("dlbig")} onMouseLeave={()=>setBtnHover(null)}
              >
                <svg width="20" height="20" viewBox="0 0 22 22" fill="none"><path d="M11 3v12M6 10l5 5 5-5M4 18h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                DOWNLOAD FOR MAC
              </a>

              <span style={{fontFamily:T.pixel,fontSize:"clamp(8px,1.8vw,9px)",color:"rgba(255,255,255,0.35)",letterSpacing:"0.08em"}}>v1.0.0 · PeckerTap.dmg · ~140.41MB </span>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <div className="pt-pipe-div"/>
      <footer style={{
        background:T.night2,borderTop:"1px solid rgba(255,255,255,0.06)",
        padding:"clamp(24px,5vw,40px) clamp(16px,5vw,40px)",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexWrap:"wrap",gap:16,
        textAlign:"center",
      }}>
        <div style={{fontFamily:T.pixel,fontSize:"clamp(14px,3vw,16px)",color:"white",letterSpacing:1}}>PeckerTap</div>
        {/* <div style={{display:"flex",gap:"clamp(16px,4vw,24px)",flexWrap:"wrap"}}>
          {["Privacy","Source","Contact"].map(l=>(
            <a key={l} href="#" style={{fontFamily:T.pixel,fontSize:"clamp(12px,2.5vw,14px)",color:"rgba(255,255,255,0.4)",textDecoration:"none",transition:"color 0.2s",letterSpacing:"0.06em",WebkitTapHighlightColor:"transparent"}}
              onMouseEnter={e=>e.currentTarget.style.color=T.yellow}
              onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.4)"}
            >{l}</a>
          ))}
        </div> */}
        <span style={{fontFamily:T.pixel,fontSize:"clamp(10px,2.2vw,12px)",color:"white",letterSpacing:"0.05em"}}>PeckerTap™ © 2026. All rights reserved.
Developed with ❤️ for players who never stop tapping.</span>
      </footer>

      {/* ── MODAL ── */}
      <GameModal open={modalOpen} onClose={closeModal}/>
    </div>
  );
}

// tiny clamp helper for inline use (avoids CSS string parsing issues)
function clamp(min,max){return`clamp(${min}px,${(min+max)/2/10}vw,${max}px)`;}
